import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Award, Target, Users, Globe, CheckCircle } from 'lucide-react';
import { companyInfo, services, certifications, naicsCodes } from '../data/mock';

export const AboutPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            About {companyInfo.name}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {companyInfo.tagline}
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            {companyInfo.description}
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              {companyInfo.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Our Services Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Service Divisions</h2>
            <p className="text-lg text-gray-600">
              Three specialized areas of expertise serving diverse client needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                  {/* Blue service titles removed */}
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{service.subtitle}</p>
                  <Link to={`/services/${service.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}`}>
                    <Button variant="outline" className="w-full">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Award className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Certifications & Credentials</h2>
            <p className="text-lg text-gray-600">
              Our team maintains industry-leading certifications to ensure the highest quality service
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((cert, index) => (
              <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Details */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Business Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Information</h2>
              <div className="space-y-4">
                <div className="p-4 bg-white rounded-lg border">
                  <h3 className="font-semibold text-gray-900 mb-2">Registration Details</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Entity ID:</span> {companyInfo.entityId}</p>
                    <p><span className="font-medium">CAGE/NCAGE Code:</span> {companyInfo.cageCode}</p>
                    <p><span className="font-medium">D-U-N-S Number:</span> {companyInfo.dunsNumber}</p>
                  </div>
                </div>
                
                <div className="p-4 bg-white rounded-lg border">
                  <h3 className="font-semibold text-gray-900 mb-2">Registration Platforms</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>• Nevada.ionwave.net</p>
                    <p>• Nevadapro.com</p>
                    <p>• Sams.gov</p>
                    <p>• Caleprocure.ca.gov</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* NAICS Codes */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">NAICS Codes</h2>
              <div className="space-y-4">
                {naicsCodes.map((naics, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg border">
                    <div className="flex items-start space-x-3">
                      <Badge variant="outline" className="mt-1">{naics.code}</Badge>
                      <div>
                        <p className="text-gray-800 font-medium">{naics.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Core Competencies</h2>
            <p className="text-lg text-gray-600">
              Comprehensive expertise across compliance, technology, and creative services
            </p>
          </div>
          
          <div className="space-y-8">
            <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">EEO Compliance & Inclusion Services</h3>
              <p className="text-gray-700">
                Ensure adherence to federal, state, and local EEO laws through compliance monitoring, 
                impartial investigations, policy development, employee training, workforce data analysis, 
                community engagement, and representation during audits to promote fairness, diversity, 
                and inclusion in the workplace.
              </p>
            </div>
            
            <div className="p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Cyber GRC & Policy Development</h3>
              <p className="text-gray-700">
                GAP analysis and policy/procedure writing for CMMC Level 1 & 2, FISMA, NIST SP 800-53, 
                PCI; Remote support for compliance assessments and remediation; process improvements; 
                Audit remediation guidance and continuous compliance monitoring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Globe className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            We welcome partnerships, collaborations, and project-based contracts across the United States. 
            Let's build something extraordinary together — remotely, effectively, and with purpose.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Schedule Your Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};